import { useCallback, useContext, useEffect, useState } from 'react';
import EntityDetailPopup from '../../../utility/EntityDetailPopup';
import { DownArrowIcon, EyeIcon, cropClasses, miscClasses, popupClasses } from '../../../utility';
import { IconButton, InputAdornment, MenuItem, TextField, Chip, Box, Menu, Typography } from '@mui/material';
import CropRunFRTLPopup from '../../../utility/popups/CropRunFRTLPopup';
import { getFrtlAliasString } from '../../../../utility/frtlUtils';
import { FRTLContext } from '../../../../context/FRTLContext';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const ROUTINE_TYPE_OPTIONS = [
  { value: 'post_harvest', label: 'Post Harvest' },
  { value: 'fine_trimming', label: 'Fine Trimming' },
  { value: 'none', label: 'None' },
];

export default function CreateBucketPopup(props) {
  const {
    open,
    onClose,
    currDarkThemeClass,
    newBucketData,
    parentValues,
    handleAddBucket,
    isEditMode,
    currentBucket,
    cropplanid,
    onEditBucket,
    onDeleteBucket,
  } = props;

  console.log('CreateBucketPopup open:', newBucketData);
  console.log('Edit mode:', isEditMode, 'Current bucket:', currentBucket);

  const [values, setValues] = useState({
    name: '',
    max_weight: '',
    weight: '',
    bucket_note: '',
    bucket_unit_of_measurement: '',
    item: '',
    bucket_categories: '',
    routine_type: 'none',
    frtl: '',
    harvests: [],
    items: newBucketData?.items || [],
    unit: newBucketData?.unit || [],
    bucket_categories_options: newBucketData?.bucket_categories || [],
    harvests_options: newBucketData?.harvests || [],
    frtlOpen: false,
  });

  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedBucketForAction, setSelectedBucketForAction] = useState(null);

  const { frtlContextValue } = useContext(FRTLContext);
  const navigate = useNavigate();

  // Initialize form with current bucket data when in edit mode
  useEffect(() => {
    if (isEditMode && currentBucket) {
      console.log('Setting form values for edit mode:', currentBucket);
      setValues(prevValues => ({
        ...prevValues,
        name: currentBucket.name || '',
        max_weight: currentBucket.max_weight || '',
        weight: currentBucket.weight || '',
        bucket_note: currentBucket.bucket_note || '',
        bucket_unit_of_measurement: currentBucket.bucket_unit_of_measurement || '',
        item: currentBucket.item || '',
        bucket_categories: currentBucket.bucket_categories || '',
        routine_type: currentBucket.routine_type || 'none',
        frtl: currentBucket.frtl || '',
        harvests: Array.isArray(currentBucket.harvests) ? currentBucket.harvests : [],
      }));
    } else if (!isEditMode) {
      // Reset to empty form for create mode
      setValues(prevValues => ({
        ...prevValues,
        name: '',
        max_weight: '',
        weight: '',
        bucket_note: '',
        bucket_unit_of_measurement: '',
        item: '',
        bucket_categories: '',
        routine_type: 'none',
        frtl: '',
        harvests: [],
      }));
    }
  }, [isEditMode, currentBucket]);

  // Update options when newBucketData changes
  useEffect(() => {
    setValues(prevValues => ({
      ...prevValues,
      harvests_options: newBucketData?.harvests || [],
      items: newBucketData?.items || [],
      unit: newBucketData?.unit || [],
      bucket_categories_options: newBucketData?.bucket_categories || [],
    }));
  }, [newBucketData]);

  const handleSetValues = useCallback((prop, value) => setValues(state => ({ ...state, [prop]: value })), []);

  const handleItemChange = useCallback(
    itemId => {
      const selectedItem = values.items.find(item => item.id === itemId);

      if (selectedItem) {
        setValues(prevState => ({
          ...prevState,
          item: itemId,
          bucket_unit_of_measurement: selectedItem.metrc_unit_of_measurement || '',
        }));
      } else {
        handleSetValues('item', itemId);
      }
    },
    [values.items, handleSetValues]
  );

  const groupedHarvests = useCallback(() => {
    const groups = {};

    values.harvests_options.forEach(harvest => {
      const metrcId = harvest.metrc_harvest_id;
      if (!groups[metrcId]) {
        groups[metrcId] = {
          metrc_harvest_id: metrcId,
          harvests: [],
          display_name: harvest.harvest_name || `Harvest ${metrcId}`,
        };
      }
      groups[metrcId].harvests.push(harvest);
    });

    return Object.values(groups);
  }, [values.harvests_options]);

  const handleHarvestChange = useCallback(
    event => {
      const selectedMetrcIds = event.target.value;
      const grouped = groupedHarvests();

      const selectedHarvestIds = [];
      selectedMetrcIds.forEach(metrcId => {
        const group = grouped.find(g => g.metrc_harvest_id === metrcId);
        if (group) {
          selectedHarvestIds.push(...group.harvests.map(h => h.id));
        }
      });

      handleSetValues('harvests', selectedHarvestIds);
    },
    [groupedHarvests, handleSetValues]
  );

  const getSelectedMetrcIds = useCallback(() => {
    const grouped = groupedHarvests();
    const selectedMetrcIds = [];

    grouped.forEach(group => {
      const hasSelectedHarvest = group.harvests.some(harvest => values.harvests.includes(harvest.id));
      if (hasSelectedHarvest) {
        selectedMetrcIds.push(group.metrc_harvest_id);
      }
    });

    return selectedMetrcIds;
  }, [groupedHarvests, values.harvests]);

  const handleSaveBucket = () => {
    const bucketData = {
      name: values.name,
      max_weight: values.max_weight,
      weight: values.weight,
      bucket_note: values.bucket_note,
      bucket_unit_of_measurement: values.bucket_unit_of_measurement,
      item: values.item,
      bucket_categories: values.bucket_categories,
      routine_type: values.routine_type,
      frtl: values.frtl,
      harvests: values.harvests,
      cropplan_id: cropplanid,
    };

    if (handleAddBucket) {
      handleAddBucket(bucketData);
    }
  };

  const handleActionMenuOpen = (event, bucket) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedBucketForAction(bucket);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedBucketForAction(null);
  };

  const handleEdit = () => {
    if (onEditBucket && selectedBucketForAction) {
      onEditBucket(selectedBucketForAction);
    }
    handleActionMenuClose();
  };

  const handleDelete = () => {
    if (onDeleteBucket && selectedBucketForAction) {
      onDeleteBucket(selectedBucketForAction);
    }
    handleActionMenuClose();
  };

  const eDPopupProps = {
    open,
    onClose,
    currDarkThemeClass,
    title: isEditMode ? `Edit Bucket: ${values.name}` : 'Create New Bucket',
    headContentText: isEditMode ? 'Update bucket information below.' : 'Create a new bucket for your crop plan.',
    onSaveClick: handleSaveBucket,
    toDisableSave: !values.name || !values.bucket_categories || !values.item || !values.frtl || !values?.max_weight,
    saveButtonText: isEditMode ? 'Update Bucket' : 'Create Bucket',
  };

  const cropPlanMenuItemProps = {
    className: `${cropClasses?.menuItem} ${currDarkThemeClass}`,
    focusVisibleClassName: miscClasses?.menuItemFocusDark,
  };

  const textFieldProp = ({ prop, inputProps = {}, otherTextProps = {} }) => ({
    autoComplete: 'off',
    className: `${popupClasses?.strainEntry?.textField} ${currDarkThemeClass}`,
    fullWidth: true,
    inputProps,
    label: '',
    onChange: ({ target: { value } }) => handleSetValues(prop, value),
    value: values?.[prop] ?? '',
    ...otherTextProps,
  });

  const bucketNameProps = {
    prop: 'name',
    otherTextProps: {
      label: 'Bucket Name',
      required: true,
      placeholder: 'Enter bucket name',
    },
  };

  const maxWeightProps = {
    prop: 'max_weight',
    otherTextProps: {
      label: 'Max Weight',
      type: 'number',
      placeholder: 'Enter maximum weight',
    },
  };

  const weightError = values.weight !== '' && values.max_weight !== '' && parseFloat(values.weight) > parseFloat(values.max_weight);

  const currentWeightProps = {
    prop: 'weight',
    otherTextProps: {
      label: 'Current Weight',
      type: 'number',
      placeholder: 'Enter current weight',
      error: weightError,
      helperText: weightError ? 'Current weight cannot exceed max weight' : undefined,
    },
  };

  const bucketNoteProps = {
    prop: 'bucket_note',
    otherTextProps: {
      label: 'Bucket Note',
      multiline: true,
      rows: 3,
      placeholder: 'Add any notes about this bucket',
    },
  };

  const unitsList =
    values.unit?.map(unit => (
      <MenuItem key={unit.id} value={unit.id} {...cropPlanMenuItemProps}>
        {unit.unit_name}
      </MenuItem>
    )) ?? [];

  if (!unitsList?.length) {
    unitsList.push(
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        No Available Units
      </MenuItem>
    );
  }

  const unitProps = {
    prop: 'bucket_unit_of_measurement',
    otherTextProps: {
      label: 'Unit of Measurement',
      select: true,
      disabled: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
    },
  };

  const itemsList =
    values.items?.map(item => (
      <MenuItem key={item.id} value={item.id} {...cropPlanMenuItemProps}>
        {item.item_name}
      </MenuItem>
    )) ?? [];

  if (!itemsList?.length) {
    itemsList.push(
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        No Available Items
      </MenuItem>
    );
  }

  const itemProps = {
    prop: 'item',
    otherTextProps: {
      label: 'Item',
      required: true,
      select: true,
      disabled: newBucketData?.items.length === 0 || isEditMode,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
      onChange: ({ target: { value } }) => handleItemChange(value),
    },
  };

  const categoriesList =
    values.bucket_categories_options?.map(category => (
      <MenuItem key={category.id} value={category.id} {...cropPlanMenuItemProps}>
        {category.name}
      </MenuItem>
    )) ?? [];

  if (!categoriesList?.length) {
    categoriesList.push(
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        No Available Categories
      </MenuItem>
    );
  }

  const categoryProps = {
    prop: 'bucket_categories',
    otherTextProps: {
      label: 'Bucket Category',
      required: true,
      disabled: isEditMode,
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
    },
  };

  const routineTypeList = ROUTINE_TYPE_OPTIONS.map(option => (
    <MenuItem key={option.value} value={option.value} {...cropPlanMenuItemProps}>
      {option.label}
    </MenuItem>
  ));

  const routineTypeProps = {
    prop: 'routine_type',
    otherTextProps: {
      label: 'Routine Type',
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
    },
  };

  const grouped = groupedHarvests();
  const selectedMetrcIds = getSelectedMetrcIds();

  const harvestMenuItemProps = {
    className: `${cropClasses?.menuItem} ${currDarkThemeClass}`,
    focusVisibleClassName: miscClasses?.menuItemFocusDark,
  };

  const harvestsList = grouped.map(group => (
    <MenuItem key={group.metrc_harvest_id} value={group.metrc_harvest_id} {...harvestMenuItemProps}>
      {group.display_name} ({group.harvests.length} harvest{group.harvests.length > 1 ? 's' : ''})
    </MenuItem>
  ));

  if (harvestsList.length === 0) {
    harvestsList.push(
      <MenuItem key={-1} value={-1} disabled {...harvestMenuItemProps}>
        No Harvests Available...
      </MenuItem>
    );
  }

  const harvestsProps = {
    prop: 'harvests',
    otherTextProps: {
      label: 'Harvests',
      select: true,
      SelectProps: {
        multiple: true,
        IconComponent: DownArrowIcon,
        value: selectedMetrcIds,
        onChange: handleHarvestChange,
        renderValue: selected => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map(metrcId => {
              const group = grouped.find(g => g.metrc_harvest_id === metrcId);
              return <Chip key={metrcId} label={`${group?.display_name || metrcId} (${group?.harvests.length || 0})`} size="small" />;
            })}
          </Box>
        ),
        MenuProps: MenuProps,
      },
    },
  };

  const handleOpenFRTLPopup = () => {
    handleSetValues('frtlOpen', true);
  };

  const handleFRTLClose = () => {
    handleSetValues('frtlOpen', false);
  };

  const frtlProps = {
    otherTextProps: {
      label: 'FRTL (Farm - Room - Table - Level)',
      value: getFrtlAliasString(values?.frtl, frtlContextValue) ?? '',
      placeholder: 'FARM - ROOM - TABLE - LEVEL',
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={handleOpenFRTLPopup}>
              <EyeIcon />
            </IconButton>
          </InputAdornment>
        ),
      },
    },
    inputProps: {
      disabled: true,
    },
  };

  const cropRunFRTLProps = {
    open: values?.frtlOpen,
    currDarkThemeClass,
    onClose: handleFRTLClose,
    handleEditFrtl: value => handleSetValues('frtl', value),
    selectedFarmId: parentValues?.farm,
    farm: +values?.frtl?.split('.')[0] ?? '',
    room: +values?.frtl?.split('.')[1] ?? '',
    table: +values?.frtl?.split('.')[2] ?? '',
    level: +values?.frtl?.split('.')[3] ?? '',
  };

  return (
    <>
      <EntityDetailPopup {...eDPopupProps}>
        {values.items.length === 0 && (
          <Typography variant="subtitle1" gutterBottom component="div" style={{ color: 'white' }}>
            <Link to="/integration/1/items?tab=item">Create Items for this strain</Link>
          </Typography>
        )}
        <TextField {...textFieldProp(categoryProps)}>{categoriesList}</TextField>
        <TextField {...textFieldProp(bucketNameProps)} />
        <TextField {...textFieldProp(itemProps)}>{itemsList}</TextField>
        <TextField {...textFieldProp(unitProps)}>{unitsList}</TextField>
        <TextField {...textFieldProp(routineTypeProps)}>{routineTypeList}</TextField>
        <TextField {...textFieldProp(maxWeightProps)} />
        <TextField {...textFieldProp(currentWeightProps)} />

        <TextField {...textFieldProp(harvestsProps)}>{harvestsList}</TextField>
        <TextField {...textFieldProp(bucketNoteProps)} />
        <TextField {...textFieldProp(frtlProps)} />
        {values?.frtlOpen && <CropRunFRTLPopup {...cropRunFRTLProps} />}
      </EntityDetailPopup>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        PaperProps={{
          className: currDarkThemeClass,
        }}
      >
        <MenuItem onClick={handleEdit} className={`${cropClasses?.menuItem} ${currDarkThemeClass}`}>
          <EditIcon sx={{ mr: 1, fontSize: '1rem' }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} className={`${cropClasses?.menuItem} ${currDarkThemeClass}`}>
          <DeleteIcon sx={{ mr: 1, fontSize: '1rem' }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}
